/**
 * Sanitize user input to prevent XSS attacks
 * Uses DOMPurify in the browser, falls back to basic sanitization in Node
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';

  // In browser environment, use DOMPurify
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    try {
      const DOMPurify = require('dompurify');
      const sanitized = DOMPurify.sanitize(input, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
        KEEP_CONTENT: true
      });
      return sanitized.trim();
    } catch (e) {
      // Fallback if DOMPurify fails to load
      console.warn('DOMPurify not available, using basic sanitization');
    }
  }

  // Basic sanitization for Node environment or as fallback
  // Remove any HTML tags and trim
  const basicSanitized = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/[<>]/g, '')
    .trim();

  return basicSanitized;
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate year of travel
 */
export function isValidYear(year: string): boolean {
  const yearNum = parseInt(year, 10);
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 10; // Allow booking up to 10 years in advance

  return !isNaN(yearNum) && yearNum >= currentYear && yearNum <= maxYear;
}

/**
 * Validate full name (at least 2 characters, only letters, spaces, hyphens, and apostrophes)
 */
export function isValidFullName(name: string): boolean {
  const nameRegex = /^[a-zA-Z\s\-']{2,100}$/;
  return nameRegex.test(name);
}

/**
 * Validate lead status
 */
export function isValidLeadStatus(status: string): status is 'Researching' | 'Ready to Book' {
  return status === 'Researching' || status === 'Ready to Book';
}

/**
 * Get current year
 */
export function getCurrentYear(): number {
  return new Date().getFullYear();
}

/**
 * Get max allowed year (current year + 10)
 */
export function getMaxYear(): number {
  return getCurrentYear() + 10;
}